import { BREAKPOINTS, COLORS, ModalContent, Size, SystemIcons, TypographyBase } from "@laerdal/life-react-components";
import { PageWidthFull } from "../../../commonComponents/PageWidthFull";
import TranslateWithAI from "../../../commonComponents/TranslateWithAI";
import { marketList } from "../../../model/constants/marketContants";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ProductResourceCard from "./ProductResourceCard";
import { useEffect, useState } from "react";
import { posthog } from "posthog-js";

const ilustration: string = require("../../../assets/concept-illustration-rita.svg").default;

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    gap:32px;
    display: flex;
    align-items: start;
`;

const ProductResourcesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    ${BREAKPOINTS.LARGE}{
        grid-template-columns: repeat(3, 1fr);
    }
`;

const ModalH2 = styled.h2`
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`;

const ModalParagraph = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: ${COLORS.text_subtle};
  display: block;
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;

  ${BREAKPOINTS.LARGE} {
    gap: 32px;
    padding: 32px 0;
  }
`;

const ModalContentText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface ProductResourcesProps {
  currentMarket: string;
  isMobile: boolean;
  categoryLink?: string;
  productLink?: string;
  knowledgeHubLink?: string;
  howToVideosLink?: string;
  userGuideLink?: string;
  partsAndAccessoriesLink?: string;
  hasDownloads?: boolean;
  hasOnboarding?: boolean;
}

const ProductResources = ({ currentMarket, isMobile, categoryLink, productLink, knowledgeHubLink, howToVideosLink, userGuideLink, partsAndAccessoriesLink, hasDownloads, hasOnboarding }: ProductResourcesProps) => {
  const { t } = useTranslation('landingPage');
  const navigate = useNavigate();
  const { state, hash, pathname } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasAnyResources = hasOnboarding || 
    hasDownloads || 
    (userGuideLink && userGuideLink.trim() !== '') || 
    (knowledgeHubLink && knowledgeHubLink.trim() !== '') || 
    (howToVideosLink && howToVideosLink.trim() !== '') || 
    (partsAndAccessoriesLink && partsAndAccessoriesLink.trim() !== '');

  const posthogData = {
    current_market: currentMarket,
    category_link: categoryLink,
    product_link: productLink
  };

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - 16, behavior: 'smooth' });
      }
    }
  }, [hash]);

  useEffect(() => {
    if (state && state.setupCompleted === true) {
      setIsModalOpen(true);
      navigate(pathname, { replace: true, state: {} });
    }
  }, [state, pathname]);

  const onSetupAssistantClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingSetupBoxClicked', posthogData);
    currentMarket !== undefined && currentMarket.length > 0
      ? navigate(`/${currentMarket}/category/${categoryLink}/product/${productLink}/onboarding`)
      : navigate(`/category/${categoryLink}/product/${productLink}/onboarding`);
  };

  const onUserGuideClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingUserGuideBoxClicked', posthogData);
    if (userGuideLink) {
      window.open(userGuideLink, '_blank');
    }
  };

  const onDownloadsClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingDownloadsBoxClicked', posthogData);
    currentMarket !== undefined && currentMarket.length > 0
      ? navigate(`/${currentMarket}/category/${categoryLink}/product/${productLink}/downloads`)
      : navigate(`/category/${categoryLink}/product/${productLink}/downloads`);
  };

  const onKnowledgeHubClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingKnowledgeHubBoxClicked', posthogData);
    if (knowledgeHubLink) {
      window.open(knowledgeHubLink, '_blank');
    }
  };

  const onHowToVideosClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingHowToVideosBoxClicked', posthogData);
    if (howToVideosLink) {
      window.location.href = howToVideosLink;
    }
  };

  const onPartsAndAccessoriesClick = () => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingPartsAndAccessoriesBoxClicked', posthogData);
    if (partsAndAccessoriesLink) {
      window.open(partsAndAccessoriesLink, '_blank');
    }
  };

  if (!hasAnyResources) {
    return null;
  }

  return (
    <>
      <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
        {marketList[currentMarket].locale != 'en' && <TranslateWithAI isMedicalProduct={categoryLink == 'medical-devices'} />}
        <PageContainer>
          <TypographyBase id="product-resources" $fontSize={32} $lineHeight={32} $fontWeight={700}>{t('Product resources')}</TypographyBase>
          <ProductResourcesGrid>
            {hasOnboarding && (
              <ProductResourceCard
                key="setup-assistant"
                id="setup-assistant"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.GearSettings color={COLORS.text_accent1} />,
                  text: t('Setup your product')
                }}
                description={t('Follow an easy step-by-step guide to get your product ready for use.')}
                linkText={t('Use setup assistant')}
                action={onSetupAssistantClick}
              />
            )}
            {userGuideLink && (
              <ProductResourceCard
                key="user-guide"
                id="user-guide"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.Document color={COLORS.text_critical} />,
                  text: t('User guide')
                }}
                description={t('Find information on features and specifications')}
                linkText={t('Browse the guide')}
                action={onUserGuideClick}
              />
            )}
            {knowledgeHubLink && (
              <ProductResourceCard
                key="knowledge-hub"
                id="knowledge-hub"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.Learner color={COLORS.text_accent1} />,
                  text: t('Learn to use your product')
                }}
                description={t('Build your confidence in using your product.')}
                linkText={t('KnowledgeHub')}
                action={onKnowledgeHubClick}
              />
            )}
            {howToVideosLink && (
              <ProductResourceCard
                key="how-to-videos"
                id="how-to-videos"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.Video color={COLORS.text_critical} />,
                  text: t('How-to videos')
                }}
                description={t('Guidance on product setup and replacements.')}
                linkText={t('View all videos')}
                action={onHowToVideosClick}
              />
            )}
            {partsAndAccessoriesLink && (
              <ProductResourceCard
                key="parts-accessories"
                id="parts-accessories"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.ShoppingCart color={COLORS.text_accent1} />,
                  text: t('Parts and Accessories')
                }}
                description={t('Find parts, accessories and consumables for this product.')}
                linkText={t('Go to shop')}
                action={onPartsAndAccessoriesClick}
              />
            )}
            {hasDownloads && (
              <ProductResourceCard
                key="downloads"
                id="downloads"
                isMobile={isMobile}
                title={{
                  icon: <SystemIcons.Download color={COLORS.text_accent1} />,
                  text: t('Downloads')
                }}
                description={t('Stay up-to-date with the latest software updates for this product.')}
                linkText={t('Go to downloads')}
                action={onDownloadsClick}
              />
            )}
          </ProductResourcesGrid>
        </PageContainer>
      </PageWidthFull>
      <ModalContent
        closeAction={() => setIsModalOpen(false)}
        title={t('Setup completed')}
        size={isMobile ? Size.Small : Size.Medium}
        isModalOpen={isModalOpen}
        footerActions={[{
          text: t('Dismiss'),
          action: () => setIsModalOpen(false),
          variant: 'primary',
          type: 'button',
        }]}
      >
        <ModalContentWrapper>
          <img src={ilustration} alt='Concept illustration' />
          <ModalContentText>
            <ModalH2>{t('Congratulations!')}</ModalH2>
            <ModalParagraph>{t('Your product setup is now complete. For further resources and support, please refer to this page.')}</ModalParagraph>
          </ModalContentText>
        </ModalContentWrapper>
      </ModalContent>
    </>
  );
};

export default ProductResources;
